import { useState, useCallback, useRef } from 'react';
import { Modal } from '@swtorbit/ui/react';
import {
  TrlImageAsset,
  asTrlDifficulty,
  TRL_DIFFICULTY_EMOJI,
  TRL_DIFFICULTY_LABELS,
  TRL_DIFFICULTY_UNRATED_EMOJI,
} from '@swtorbit/shared-socket-events';
import { API_URL } from '../../../config';
import { usePlatformLocate } from '../../../hooks/usePlatformLocate';
import { PlatformLocationPanels } from './PlatformLocationPanels';
import { useTrlImageLibrary } from './TrlImageLibraryContext';
import { MetadataForm, type MetadataFormValues } from './MetadataForm';
import { ScreenshotCropStage, type ScreenshotCropStageHandle } from './ScreenshotCropStage';

const STAT_CARD: React.CSSProperties = {
  background: 'color-mix(in srgb, var(--color-white) 5%, transparent)',
  border: '1px solid color-mix(in srgb, var(--color-white) 12%, transparent)',
  borderRadius: '6px',
  padding: '10px 12px',
};

interface ImageLightboxProps {
  image: TrlImageAsset;
  onClose: () => void;
}

export function ImageLightbox({ image, onClose }: ImageLightboxProps) {
  const { updateImage, deleteImage, setImageShared } = useTrlImageLibrary();

  // Where it was taken, from the platform: names, faction and both maps with a
  // pin.
  const locate = usePlatformLocate(image.logAreaId, image.x, image.y, image.z, 0);

  const isReadOnly = image.isOwn === false;

  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const cropStageRef = useRef<ScreenshotCropStageHandle>(null);

  const handleSave = useCallback(
    async (values: MetadataFormValues) => {
      // Pull a framed replacement if one was staged in edit mode; null → the
      // screenshot is left as-is and only metadata is updated.
      const replacement = await cropStageRef.current?.getCroppedFile();
      await updateImage(image.id, {
        name: values.name,
        tags: values.tags,
        logAreaId: values.logAreaId,
        x: values.x,
        y: values.y,
        z: values.z,
        facing: values.facing,
        difficulty: values.difficulty,
        faction: values.faction,
        image: replacement ?? undefined,
      });
      setIsEditing(false);
    },
    [updateImage, image.id],
  );

  const handleDelete = useCallback(async () => {
    const label = image.name ? `"${image.name}"` : 'this screenshot';
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    setError(null);
    try {
      await deleteImage(image.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }, [deleteImage, image.id, image.name, onClose]);

  const handleToggleShare = useCallback(async () => {
    setError(null);
    setIsSharing(true);
    try {
      await setImageShared(image.id, !image.isShared);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sharing');
    } finally {
      setIsSharing(false);
    }
  }, [setImageShared, image.id, image.isShared]);

  return (
    <Modal open onClose={onClose} size="full" bodyClassName="p-0" title={image.name || image.area}>
        <div className="d-flex h-100">
          {/* 2/3 — image (a replace-capable crop stage while editing) */}
          {isEditing && !isReadOnly ? (
            <ScreenshotCropStage
              ref={cropStageRef}
              existingImageUrl={`${API_URL}${image.url}`}
              enablePaste
              style={{ flex: '2 1 0' }}
            />
          ) : (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ flex: '2 1 0', background: 'var(--color-black)', overflow: 'hidden' }}
            >
              <img
                src={`${API_URL}${image.url}`}
                alt={image.name || image.area}
                className="dropzone__preview"
              />
            </div>
          )}

          {/* 1/3 — sidebar */}
          <div
            className="overflow-y-auto"
            style={{ flex: '1 1 0', padding: '20px', borderLeft: '1px solid var(--color-teal-dark)' }}
          >
            {isEditing && !isReadOnly ? (
              <MetadataForm
                mode="edit"
                idPrefix={`trl-edit-${image.id}`}
                initial={{
                  name: image.name,
                  difficulty: image.difficulty,
                  faction: image.faction,
                  tags: image.tags,
                  logAreaId: image.logAreaId,
                  x: image.x,
                  y: image.y,
                  z: image.z,
                  facing: image.facing,
                }}
                submitLabel="Save"
                submitBusyLabel="Saving…"
                onSubmit={handleSave}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <div className="d-flex flex-column gap-sm">

                {/* Difficulty */}
                {image.difficulty && (
                  <div className="d-flex align-items-center gap-xs" style={STAT_CARD}>
                    <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                      {asTrlDifficulty(image.difficulty)
                        ? TRL_DIFFICULTY_EMOJI[asTrlDifficulty(image.difficulty)!]
                        : TRL_DIFFICULTY_UNRATED_EMOJI}
                    </span>
                    <div>
                      <p className="fs-xs text-slate m-0 text-uppercase font-semibold">Difficulty</p>
                      {/* An unrecognised value is echoed back verbatim rather than
                          relabelled, so a stray rating is visible to the admin. */}
                      <p className="text-white m-0 fw-bold">
                        {asTrlDifficulty(image.difficulty)
                          ? TRL_DIFFICULTY_LABELS[asTrlDifficulty(image.difficulty)!]
                          : image.difficulty}
                      </p>
                    </div>
                  </div>
                )}

                {/* Location */}
                <PlatformLocationPanels {...locate} screenshotFaction={image.faction} />

                {/* Coordinates */}
                <div>
                  <p className="fs-xs text-slate m-0 text-uppercase font-semibold" style={{ marginBottom: '6px' }}>Coordinates</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                    {[{ label: 'X', value: image.x }, { label: 'Y', value: image.y }, { label: 'Z', value: image.z }].map(({ label, value }) => (
                      <div key={label} className="text-center" style={STAT_CARD}>
                        <p className="fs-xs text-slate m-0 text-uppercase font-semibold">{label}</p>
                        <p className="text-white m-0 fw-bold">{value}</p>
                      </div>
                    ))}
                  </div>
                  {image.facing !== undefined && (
                    <div className="text-center mt-xs" style={STAT_CARD}>
                      <p className="fs-xs text-slate m-0 text-uppercase font-semibold">Facing</p>
                      <p className="text-white m-0 fw-bold">↻ {image.facing}</p>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {image.tags.length > 0 && (
                  <div>
                    <p className="fs-xs text-slate m-0 text-uppercase font-semibold" style={{ marginBottom: '6px' }}>Tags</p>
                    <div className="d-flex flex-wrap gap-2xs">
                      {image.tags.map((tag) => (
                        <span key={tag} className="badge badge--pill">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Author + Sharing */}
                <div className="d-flex flex-column gap-2xs">
                  {image.createdByName && (
                    <span className="fs-sm text-slate">👤 {image.createdByName}</span>
                  )}
                  {isReadOnly ? (
                    <span className="fs-sm text-aqua-dark">🔗 Shared by another admin · read-only</span>
                  ) : image.isShared ? (
                    <span className="fs-sm text-green-dark">🌐 Shared to all admins</span>
                  ) : (
                    <span className="fs-sm text-slate">🔒 Private to you</span>
                  )}
                </div>

                {error && <p className="text-red fs-sm m-0">{error}</p>}

                {!isReadOnly && (
                  <>
                    <hr className="m-0" style={{ borderColor: 'color-mix(in srgb, var(--color-white) 12%, transparent)' }} />
                    <div className="d-flex gap-xs flex-wrap" style={{ justifyContent: 'center' }}>
                      <button className="btn btn--aqua btn--sm" onClick={() => setIsEditing(true)}>✏️ Edit</button>
                      <button
                        className={`btn btn--sm ${image.isShared ? 'btn--outline btn--slate' : 'btn--green'}`}
                        onClick={handleToggleShare}
                        disabled={isSharing}
                        title={image.isShared ? 'Stop sharing with other admins' : 'Share with all admins'}
                      >
                        {isSharing ? '…' : image.isShared ? '🚫 Un-share' : '🌐 Share'}
                      </button>
                      <button className="btn btn--red btn--sm" onClick={handleDelete}>🗑️ Delete</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
  );
}
